import { createAction, createReducer } from "@reduxjs/toolkit";
import { Bestek } from "@/models/bestek";
import { BestekFilterParams, initialBestekFilterParams, mapFilterValueToQueryParam } from "@/components/bestek-filter/bestek-filter-params";
import LocalStorageHelper from "@/localstorage"; // Import de helper

export interface BestekFilterOptiesResponseData {
    provincies: string[],
    gemeentes: string[],
    woonplaatsen: string[],
    jaarversies: string[]
}

export interface BestekFilterState extends BestekFilterParams {
    opties: BestekFilterOptiesResponseData | null,
    result: Bestek[] | null,
    query_params: { [key: string]: any },
    filterVisible: boolean
}

type BestekFilterKey = keyof BestekFilterParams;

// Verbeterde helper functie met betere debugging
const getInitialFilterVisibility = (): boolean => {
    try {
        const storageInfo = LocalStorageHelper.getStorageInfo();
        console.log('Storage info:', storageInfo);
        
        const stored = LocalStorageHelper.getItem('bestekFilterVisible');
        console.log('Stored filter visibility value:', stored);
        
        if (stored === null) {
            console.log('No stored value found, defaulting to true');
            return true;
        }
        
        const result = stored !== 'false';
        console.log('Parsed filter visibility:', result);
        return result;
    } catch (error) {
        console.warn('Error reading filter visibility from storage:', error);
        return true;
    }
};

// Verbeterde helper functie om localStorage te schrijven
const saveFilterVisibility = (visible: boolean): void => {
    try {
        const value = visible.toString();
        LocalStorageHelper.setItem('bestekFilterVisible', value);
        console.log('Saved filter visibility:', value, 'Storage type:', LocalStorageHelper.getStorageInfo().type);
        
        // Verificatie dat het opslaan werkte
        const readBack = LocalStorageHelper.getItem('bestekFilterVisible');
        if (readBack !== value) {
            console.warn('Storage verification failed! Saved:', value, 'Read back:', readBack);
        }
    } catch (error) {
        console.warn('Error saving filter visibility to storage:', error);
    }
};

const initialState: BestekFilterState = Object.assign({}, initialBestekFilterParams, {
    opties: null,
    result: null,
    query_params: {},
    filterVisible: getInitialFilterVisibility()
});

export const bestekFilterActions = {
    reset: createAction('bestek-filter/reset'),
    setResult: createAction<Bestek[]>('bestek-filter/set-result'),
    setFilterOpties: createAction<BestekFilterOptiesResponseData>('bestek-filter/set-filter-opties'),
    setValue: createAction<[ BestekFilterKey, any ]>('bestek-filter/set-value'),
    toggleFilterVisibility: createAction('bestek-filter/toggle-filter-visibility'),
    // Nieuwe action om filter visibility direct te zetten (handig voor debugging)
    setFilterVisibility: createAction<boolean>('bestek-filter/set-filter-visibility')
}

export const bestekFilterReducer = createReducer(
    initialState,
    (builder) => {
        builder.addCase(bestekFilterActions.setResult, (state, action) => {
            state.result = action.payload;
        }).addCase(bestekFilterActions.reset, (state, action) => {
            Object.keys(initialBestekFilterParams).forEach((key: string) => {
                const filterKey = key as keyof BestekFilterParams;
                (state as any)[filterKey] = (initialBestekFilterParams as any)[filterKey];
            })
            state.result = null;
            state.query_params = {}
            // Filter visibility blijft behouden bij reset
        }).addCase(bestekFilterActions.setFilterOpties, (state, action) => {
            state.opties = action.payload;
        }).addCase(bestekFilterActions.setValue, (state, action) => {
            const [ key, value ] = action.payload;
            (state as any)[key] = value;

            let query_params = { ... state.query_params }
            const [ query_key, query_value ] = mapFilterValueToQueryParam(key as keyof BestekFilterParams, value);
            if (query_key !== null) {
                if (query_value !== null) {
                    query_params[query_key] = query_value;
                } else if (query_key in query_params) {
                    delete query_params[query_key];
                }
            }
            state.query_params = query_params;
        }).addCase(bestekFilterActions.toggleFilterVisibility, (state, action) => {
            console.log('toggleFilterVisibility action triggered, current state:', state.filterVisible);
            state.filterVisible = !state.filterVisible;
            console.log('New filter visibility state:', state.filterVisible);
            saveFilterVisibility(state.filterVisible);
        }).addCase(bestekFilterActions.setFilterVisibility, (state, action) => {
            console.log('setFilterVisibility action triggered, new value:', action.payload);
            state.filterVisible = action.payload;
            saveFilterVisibility(state.filterVisible);
        });
    }
)